import { z } from 'zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from './client';
import { newIdempotencyKey } from './engagement-requirements';

/**
 * Client facts, read and written through an engagement (T-0171 API, OQ-W).
 *
 * Contract: `apps/api/src/routes/client-facts.ts`. The facts live on the
 * CLIENT; the engagement reads them and keeps no copy. A new value never
 * edits the old one: the server inserts a row and supersedes the current
 * one, so the history stays. Every value carries its source and its
 * as-of date. Nothing here is filled by AI.
 */

export const FACT_SOURCE_KINDS = ['manual', 'document', 'evidence'] as const;
export type FactSourceKind = (typeof FACT_SOURCE_KINDS)[number];

const FactSourceSchema = z.object({
  kind: z.enum(FACT_SOURCE_KINDS),
  label: z.string(),
  document_id: z.string().optional(),
  page: z.number().optional(),
  evidence_number: z.string().optional(),
  note: z.string().optional(),
  restricted: z.literal(true).optional(),
});
export type FactSource = z.infer<typeof FactSourceSchema>;

const ClientFactSchema = z.object({
  id: z.string().nullable(),
  fact_key: z.string(),
  label: z.string(),
  value: z.string().nullable(),
  as_of: z.string().nullable(),
  source: FactSourceSchema.nullable(),
  recorded_by_name: z.string().nullable(),
  recorded_at: z.string().nullable(),
});
export type ClientFact = z.infer<typeof ClientFactSchema>;

const ClientFactsSchema = z.object({
  client_id: z.string(),
  can_edit: z.boolean(),
  facts: z.array(ClientFactSchema),
});
export type ClientFacts = z.infer<typeof ClientFactsSchema>;

export interface PutClientFactBody {
  value: string;
  as_of: string;
  source: {
    kind: FactSourceKind;
    note?: string;
    document_id?: string;
    page?: number;
    evidence_number?: string;
  };
  /** The value being replaced (null: there was none), so a concurrent write is refused, not overwritten. */
  base_fact_id: string | null;
}

const PutResultSchema = z.object({ fact: ClientFactSchema, changed: z.boolean() });

function base(engagementId: string): string {
  return `/api/v1/engagements/${encodeURIComponent(engagementId)}/client-facts`;
}

export const clientFactKeys = {
  list: (engagementId: string) => ['client-facts', engagementId] as const,
};

export function useClientFactsQuery(engagementId: string, enabled = true) {
  return useQuery({
    queryKey: clientFactKeys.list(engagementId),
    queryFn: () => apiClient(base(engagementId), ClientFactsSchema),
    enabled,
    staleTime: 30_000,
    retry: false,
  });
}

export function putClientFact(engagementId: string, factKey: string, body: PutClientFactBody) {
  return apiClient(`${base(engagementId)}/${encodeURIComponent(factKey)}`, PutResultSchema, {
    method: 'PUT',
    body,
    idempotencyKey: newIdempotencyKey(),
  });
}

/** Saves several facts one after another; stops at the first refusal. */
export function useSaveClientFactsMutation(engagementId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (writes: ReadonlyArray<{ factKey: string; body: PutClientFactBody }>) => {
      const saved: string[] = [];
      for (const w of writes) {
        try {
          await putClientFact(engagementId, w.factKey, w.body);
        } catch (error) {
          throw Object.assign(error as Error, { factKey: w.factKey, saved });
        }
        saved.push(w.factKey);
      }
      return saved;
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: clientFactKeys.list(engagementId) });
      // The Overview counts the facts that are set.
      void qc.invalidateQueries({ queryKey: ['discovery', engagementId, 'summary'] });
    },
  });
}
